import { Link } from 'react-router-dom';

import type { Tournament } from '@/types/tournament';
import { cn } from '@/lib/cn';
import { cdnImage } from '@/lib/cdnImage';
import { StatusBadge } from './StatusBadge';

interface TournamentCardProps {
  tournament: Tournament;
  variant?: 'compact' | 'full';
  className?: string;
}

export function TournamentCard({ tournament, variant = 'full', className }: TournamentCardProps) {
  // Mirror mobile-app/src/components/tournaments/tournament-card.tsx — uses
  // `participant_count` (post-checkin "real" participants), not the historic
  // `registered_participant_count`.
  const seatLabel = `${tournament.participant_count ?? tournament.registered_participant_count}/${tournament.max_member}`;

  if (variant === 'compact') {
    return (
      <Link
        to={`/tournaments/${tournament.id}`}
        className={cn(
          'flex w-64 shrink-0 flex-col overflow-hidden rounded-xl bg-bg-secondary',
          'transition-transform active:scale-[0.98]',
          className,
        )}
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-bg-tertiary">
          {tournament.image && (
            <img
              src={cdnImage(tournament.image)}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          )}
          <StatusBadge status={tournament.status} className="absolute right-2 top-2" />
        </div>
        <div className="flex flex-1 flex-col gap-1 p-3">
          <h3 className="line-clamp-2 text-sm font-semibold leading-tight text-fg">
            {tournament.title}
          </h3>
          <p className="text-xs text-fg-muted">{seatLabel} players</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/tournaments/${tournament.id}`}
      className={cn(
        'flex gap-3 rounded-xl bg-bg-secondary p-3',
        'transition-opacity active:opacity-80',
        className,
      )}
    >
      <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-bg-tertiary">
        {tournament.image && (
          <img
            src={cdnImage(tournament.image)}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        <h3 className="line-clamp-2 text-sm font-semibold leading-tight text-fg">
          {tournament.title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-fg-muted">
          <StatusBadge status={tournament.status} />
          <span>{seatLabel} players</span>
        </div>
      </div>
    </Link>
  );
}
